import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Loading, AlertService, Theme, ExcelIcon, Button, DragDrop } from '@spoiler-alert/ui-library';
import SheetUploadService from '../../services/upload-sheet-service';

const allowedFileTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

const useStyles = createUseStyles(
  {
    uploadBox: {
      padding: 16,
      border: `1px solid ${Theme.grey30}`,
      backgroundColor: Theme.white,
      marginBottom: 24,
      borderRadius: 4,
    },
    dropAreaBase: {
      height: 118,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 24,
      padding: 24,
      borderRadius: 4,
      border: `dashed 1px ${Theme.teal}`,
    },
    dropArea: {
      extend: 'dropAreaBase',
      backgroundColor: Theme.teal5,
    },
    dropAreaActive: {
      extend: 'dropAreaBase',
      backgroundColor: 'rgba(78, 170, 193, 0.3)',
    },
    actionContainer: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    excelIcon: {
      marginRight: 16,
    },
    uploadButton: {
      marginRight: 8,
    },
    instructionText: {
      display: 'flex',
      flexDirection: 'column',
      fontFamily: Theme.fontFamily,
      fontSize: 14,
      margin: '24px 0',
    },
    instructionTitle: {
      fontSize: 16,
      fontWeight: 500,
    },
    uploadInstruction: {
      extend: 'instructionTitle',
      '@media (max-width: 1135px)': {
        marginLeft: 90,
      },
    },
    container: {
      height: '100%',
    },
    boldText: {
      fontWeight: 'bold',
      display: 'inline',
    },
    fields: {
      display: 'inline',
    },
    bulletList: {
      listStyleType: 'square',
      listStylePosition: 'inside',
    },
    instructionDetail: {
      margin: '10px 0 0 0',
      fontSize: 14,
      fontStyle: 'italic',
    },
  },
  { name: 'UploadSheet' },
);

const errorStrings = {
  INVALID_TYPE: 'Your sheet was not submitted due to an incorrect file type. Sheets must be in excel (XLSX) format.',
  SINGLE_FILE_ONLY: 'You can only upload one file at a time, please take the time to upload each file separately',
};

const BulkInventoryEditor = () => {
  const classes = useStyles();
  const [uploadKey, setUploadKey] = useState(0);
  const [fileInput, setFileInput] = useState();
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const showError = (errorMessage) => {
    AlertService.alert({ type: 'warning', message: <span>{errorMessage}</span>, autoDismiss: false });
  };

  const showSuccess = () => {
    AlertService.alert({
      type: 'success',
      message: <span>Inventory updates processed successfully</span>,
      autoDismiss: true,
      dismissDelay: 3000,
    });
  };

  const handleFileChange = (ev) => {
    setDragActive(false);
    const fileList = ev.target.files || ev.dataTransfer.files;
    const files = Array.from(fileList);

    if (!files.every((file) => allowedFileTypes.includes(file.type))) {
      showError(errorStrings.INVALID_TYPE);
    } else {
      SheetUploadService.importSheet(files[0], ev.target.value || files[0].name).then((r) => {
        setLoading(true);
        if (r.success) {
          showSuccess();
        } else {
          showError(r.errorMessage);
        }
        setLoading(false);
        // Rotating this key resets the file input element
        setUploadKey(uploadKey + 1);
      });
    }
  };

  const handleDropError = (errorCode, message) => {
    if (errorStrings[errorCode]) showError(errorStrings[errorCode]);
    else showError(message);
    setDragActive(false);
  };

  const uploadClick = () => {
    fileInput.click();
  };

  return (
    <div className={classes.container}>
      <div className={classes.instructionText}>
        <h3>Excel File Format</h3>
        <div className={classes.fields}>
          <div>Your Excel file can contain the following columns for updating:</div>
          <span className={classes.boldText}> Inventory ID</span>,<span className={classes.boldText}> Unit Cost</span>,
          <span className={classes.boldText}> Unit Price</span>,
          <span className={classes.boldText}> Unit Net Weight</span>,
          <span className={classes.boldText}> Unit Gross Weight</span>,
          <span className={classes.boldText}> Uploaded Reserve Price</span>, and
          <span className={classes.boldText}> Quantity</span>
        </div>
        <span className={classes.instructionDetail}>
          Columns may be omitted if you do not wish to update those fields.
        </span>
        <h3>What Can Be Updated</h3>
        <span>❌ Quantity changes blocked for awarded inventory</span>
        <span>✅ All other fields can be updated regardless of inventory status</span>
        <h3>What Happens Automatically</h3>
        <ul className={classes.bulletList}>
          <li>Total costs/prices recalculated when unit values change</li>
          <li>Listing quantities reduced if inventory quantity is reduced</li>
          <li>Offer reserve prices updated when uploaded reserve price changes</li>
          <li>Transaction costs/prices updated to match new inventory values</li>
        </ul>
      </div>
      <div className={classes.uploadBox}>
        <DragDrop
          className={dragActive ? classes.dropAreaActive : classes.dropArea}
          accepts={allowedFileTypes}
          onDrop={handleFileChange}
          onError={handleDropError}
          onDragOver={() => setDragActive(true)}
          onDragLeave={() => setDragActive(false)}
        >
          <div className={classes.actionContainer}>
            {!dragActive && <ExcelIcon className={classes.excelIcon} />}
            {!dragActive && (
              <Button onClick={uploadClick} secondary className={classes.uploadButton}>
                Browse for File
              </Button>
            )}
            {(!dragActive && (
              <span className={classes.uploadInstruction}>or drag & drop your excel sheet here to submit</span>
            )) || <span className={classes.instructionTitle}>Drop your file to submit</span>}
          </div>
        </DragDrop>
        <input
          type="file"
          id="sheet-upload"
          key={`upload-${uploadKey}`}
          onChange={handleFileChange}
          accept={allowedFileTypes.join(',')}
          style={{ display: 'none' }}
          ref={(el) => setFileInput(el)}
        />
      </div>{' '}
      {loading && <Loading loading={loading} size="large" />}
    </div>
  );
};

export default BulkInventoryEditor;
